// Pure helpers for Step 2 (Map Columns). Auto-suggests a target field per file
// column via keyword matching. Deliberately much simpler than
// SmartImport/steps/step2Helpers.ts's section/field resolution — there are only
// 4 fixed targets here, no dynamic per-company field tree.

import type { ColumnMapping, ColumnTarget } from './wizardReducer';

// The FIRST keyword in each list is the canonical name for that target (matches
// its own category label) — an exact header match to it outranks an exact match
// to any synonym, which in turn outranks a mere substring hit. This ordering
// matters: a column literally named "Class" must win the 'class' target over one
// literally named "Department", even though "department" is a longer keyword —
// without the canonical-first tiering, the longer synonym's exact match would
// otherwise score higher than the shorter canonical one.
const KEYWORDS: Record<Exclude<ColumnTarget, 'skip'>, string[]> = {
	employee: ['employee', 'employee name', 'name', 'worker', 'staff'],
	percent: ['% of hours', 'percent', 'pct', 'allocation', 'hours', '%'],
	customer: ['customer', 'client', 'job', 'project'],
	class: ['class', 'department', 'dept'],
};

const PERCENT_VALUE_RE = /^\s*\d+(\.\d+)?\s*%\s*$/;

const CANONICAL_EXACT_BONUS = 3000;
const SYNONYM_EXACT_BONUS = 2000;

// Score a single header against one target's keyword list. Tiered: exact match
// to the canonical (first) keyword > exact match to a synonym > mere substring
// hit (scored by keyword length, so a more specific keyword wins ties).
function scoreHeaderForTarget(header: string, target: Exclude<ColumnTarget, 'skip'>): number {
	const h = header.toLowerCase().trim();
	const keywords = KEYWORDS[target];
	let best = 0;
	keywords.forEach((kw, idx) => {
		if (h === kw) {
			const bonus = idx === 0 ? CANONICAL_EXACT_BONUS : SYNONYM_EXACT_BONUS;
			best = Math.max(best, bonus + kw.length);
		} else if (h.includes(kw)) {
			best = Math.max(best, kw.length);
		}
	});
	return best;
}

/**
 * Build the initial column-mapping rows for Step 2. Every header is scored
 * against every target; candidates are assigned greedily by descending score,
 * at most one column per target and one target per column. Anything left over
 * defaults to 'skip'.
 */
export function suggestColumnMappings(
	headers: string[],
	sampleValuesByHeader?: Record<string, string[]>
): ColumnMapping[] {
	const mappings: ColumnMapping[] = headers.map((header, colIndex) => ({
		colIndex,
		sourceHeader: header,
		target: 'skip',
		sampleValues: sampleValuesByHeader?.[header] ?? [],
	}));

	const targets = Object.keys(KEYWORDS) as Array<Exclude<ColumnTarget, 'skip'>>;
	const candidates: Array<{ colIndex: number; target: Exclude<ColumnTarget, 'skip'>; score: number }> =
		[];

	headers.forEach((header, colIndex) => {
		targets.forEach((target) => {
			const score = scoreHeaderForTarget(header, target);
			if (score > 0) candidates.push({ colIndex, target, score });
		});
	});

	candidates.sort((a, b) => b.score - a.score);

	const claimedCols = new Set<number>();
	const claimedTargets = new Set<ColumnTarget>();

	for (const c of candidates) {
		if (claimedCols.has(c.colIndex) || claimedTargets.has(c.target)) continue;
		claimedCols.add(c.colIndex);
		claimedTargets.add(c.target);
		mappings[c.colIndex].target = c.target;
	}

	// No header named like a percentage: fall back to the values. A leftover
	// column whose samples are all "27%"-style is the % of Hours — e.g. the
	// second column of "Customer,Class,Class" (ENG-499).
	if (!claimedTargets.has('percent')) {
		const percentLike = mappings.find(
			(m) =>
				m.target === 'skip' &&
				m.sampleValues.length > 0 &&
				m.sampleValues.every((v) => PERCENT_VALUE_RE.test(v))
		);
		if (percentLike) percentLike.target = 'percent';
	}

	return mappings;
}

export const TARGET_LABELS: Record<ColumnTarget, string> = {
	employee: 'Employee',
	percent: '% of Hours',
	customer: 'Customer',
	class: 'Class',
	skip: 'Skip',
};

export const TARGET_COLORS: Record<ColumnTarget, string> = {
	employee: 'blue',
	percent: 'green',
	customer: 'gold',
	class: 'purple',
	skip: 'default',
};

/** Up to 2 non-blank sample values for a column, for the "Sample Data" cell. */
export function sampleValuesForColumn(
	rawRows: Array<Record<string, unknown>>,
	header: string,
	max = 2
): string[] {
	const out: string[] = [];
	for (const row of rawRows) {
		const v = String(row[header] ?? '').trim();
		if (v !== '') out.push(v);
		if (out.length >= max) break;
	}
	return out;
}
